import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { useTranslation } from "react-i18next";

// V3 Components
import NavbarV3 from "./components/NavbarV3";

// V3 Pages
import HomeV3 from "./pages/HomeV3";
import MintNFTV3 from "./pages/MintNFTV3";
import MyAssetsV3 from "./pages/MyAssetsV3";
import DrawResultsV3 from "./pages/DrawResultsV3";
import MarketplaceV3 from "./pages/MarketplaceV3";
import AdminV3 from "./pages/AdminV3";
import CapabilitiesV3 from "./pages/CapabilitiesV3";

// V3 Context
import { Web3ProviderV3 } from "./contexts/Web3ContextV3";

// V3 Styles
import "./styles/globals-v3.css";

/**
 * V3 Main Application Component - Black & White Premium Theme
 */
function AppV3() {
  const currentPath = window.location.pathname.replace(/\/+$/, "") || "/";
  return currentPath === "/" || currentPath === "/capabilities" ? (
    <CapabilitiesV3 />
  ) : (
    <LegacyAppV3 />
  );
}

function LegacyAppV3() {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate initial loading
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 800);

    return () => clearTimeout(timer);
  }, []);

  // Loading screen - black & white theme
  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          {/* Loading animation */}
          <div className="mb-8">
            <div className="w-24 h-24 mx-auto relative">
              {/* Outer ring */}
              <div className="absolute inset-0 border-4 border-gray-800 animate-spin"></div>
              {/* Inner ring */}
              <div
                className="absolute inset-2 border-4 border-white animate-spin"
                style={{
                  animationDuration: "1.5s",
                  animationDirection: "reverse",
                }}
              ></div>
              {/* Center dot */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-4 h-4 bg-white"></div>
              </div>
            </div>
          </div>

          {/* Title */}
          <h2 className="text-4xl font-black text-white mb-3 tracking-tight">
            NFT LOTTERY
          </h2>
          <p className="text-gray-400 text-lg mb-8">{t("common.loading")}</p>

          {/* Progress dots */}
          <div className="flex justify-center gap-2">
            <div className="w-2 h-2 bg-white animate-pulse"></div>
            <div
              className="w-2 h-2 bg-white animate-pulse"
              style={{ animationDelay: "0.2s" }}
            ></div>
            <div
              className="w-2 h-2 bg-white animate-pulse"
              style={{ animationDelay: "0.4s" }}
            ></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Web3ProviderV3>
      <Router>
        <div className="min-h-screen bg-black">
          {/* Navigation bar */}
          <NavbarV3 />

          {/* Main content area */}
          <main>
            <Routes>
              {/* Home */}
              <Route path="/" element={<HomeV3 />} />

              {/* NFT Minting */}
              <Route path="/mint" element={<MintNFTV3 />} />

              {/* My Assets */}
              <Route path="/my-assets" element={<MyAssetsV3 />} />

              {/* Draw Results */}
              <Route path="/draws" element={<DrawResultsV3 />} />

              {/* Marketplace */}
              <Route path="/marketplace" element={<MarketplaceV3 />} />

              {/* Admin Panel */}
              <Route path="/admin" element={<AdminV3 />} />

              {/* 404 */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </main>

          {/* Toast notifications - Black & White theme */}
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 3000,
              style: {
                background: "#0A0A0A",
                color: "#FFFFFF",
                border: "2px solid #2A2A2A",
                borderRadius: "0",
                padding: "16px",
                fontFamily: "Inter, sans-serif",
                fontSize: "14px",
                boxShadow: "0 8px 12px 0 rgba(0, 0, 0, 0.6)",
              },
              success: {
                iconTheme: {
                  primary: "#FFFFFF",
                  secondary: "#000000",
                },
                style: {
                  border: "2px solid #FFFFFF",
                },
              },
              error: {
                iconTheme: {
                  primary: "#FFFFFF",
                  secondary: "#000000",
                },
                style: {
                  border: "2px solid #666666",
                },
              },
              loading: {
                iconTheme: {
                  primary: "#FFFFFF",
                  secondary: "#000000",
                },
              },
            }}
          />
        </div>
      </Router>
    </Web3ProviderV3>
  );
}

/**
 * 404 Page Component
 */
const NotFound = () => {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-9xl font-black mb-4">{t("notFound.title")}</h1>
        <p className="text-2xl text-gray-400 mb-8">{t("notFound.subtitle")}</p>
        <a href="/" className="btn-primary px-8 py-3 inline-block">
          {t("notFound.goHome")}
        </a>
      </div>
    </div>
  );
};

export default AppV3;
